import {
  PdfTemplateDocument,
  PdfTemplateField,
  PdfPageRect,
  PdfTemplateBuilderState,
  PdfTemplateSummary,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  applyRadioGroupIsRequired,
  applyCheckboxGroupIsRequired,
  getRadioGroupFields,
  getCheckboxGroupFields,
  getRadioGroupId,
  getCheckboxGroupId,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/formFields';
import {
  canSetAutoFillAlias,
  canToggleFieldOptional,
  fieldSupportsAlignment,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldTypes';
import {
  getFieldHistoryScopeByScopeId,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldHistory';

function getRadioGroupFieldId(fieldId: string) {
  return `radio-group-${fieldId}`;
}

function getCheckboxGroupFieldId(fieldId: string) {
  return `checkbox-group-${fieldId}`;
}

type OptionGroupType = 'radioGroup' | 'checkboxGroup';

function isOptionGroupType(type: string): type is OptionGroupType {
  return type === 'radioGroup' || type === 'checkboxGroup';
}

function getOptionGroupId(field: PdfTemplateField) {
  return field.type === 'checkboxGroup'
    ? getCheckboxGroupId(field)
    : getRadioGroupId(field);
}

function getOptionGroupFields(
  fields: PdfTemplateField[],
  field: PdfTemplateField,
) {
  return field.type === 'checkboxGroup'
    ? getCheckboxGroupFields(fields, getCheckboxGroupId(field))
    : getRadioGroupFields(fields, getRadioGroupId(field));
}

function applyOptionGroupIsRequired(
  groupFields: PdfTemplateField[],
  fieldType: OptionGroupType,
  isRequired: boolean,
) {
  return fieldType === 'checkboxGroup'
    ? applyCheckboxGroupIsRequired(groupFields, isRequired)
    : applyRadioGroupIsRequired(groupFields, isRequired);
}

function getGroupIdForFieldType(field: PdfTemplateField, fieldType: string) {
  if (fieldType === 'radioGroup') {
    return field.groupId ?? getRadioGroupFieldId(field.id);
  }
  if (fieldType === 'checkboxGroup') {
    return field.groupId ?? getCheckboxGroupFieldId(field.id);
  }
  return null;
}

function getUniqueFieldName(
  fieldName: string,
  existingNames: string[],
) {
  if (!existingNames.includes(fieldName)) {
    return fieldName;
  }

  let suffix = 2;
  let uniqueName = `${fieldName} ${suffix}`;

  while (existingNames.includes(uniqueName)) {
    suffix += 1;
    uniqueName = `${fieldName} ${suffix}`;
  }

  return uniqueName;
}

function getTargetFieldIds(state: PdfTemplateBuilderState): Set<string> {
  if (state.selectedFieldIds.length > 0) {
    return new Set(state.selectedFieldIds);
  }

  if (state.selectedFieldId) {
    return new Set([state.selectedFieldId]);
  }

  return new Set();
}

function getFieldNames(
  fields: PdfTemplateField[],
  excludedFieldIds: string[] = [],
) {
  return fields
    .filter((field) => !excludedFieldIds.includes(field.id))
    .map((field) => field.name);
}

export function getNextDefaultFieldName(existingNames: string[]) {
  const nextIndex = existingNames.reduce((maxIndex, name) => {
    const match = /^New (\d+)$/.exec(name);
    return match ? Math.max(maxIndex, Number(match[1])) : maxIndex;
  }, 0) + 1;

  return `New ${nextIndex}`;
}

function createFieldWithUniqueName(
  field: PdfTemplateField,
  existingFields: PdfTemplateField[],
) {
  const existingNames = getFieldNames(existingFields);
  const nextFieldName = /^New \d+$/.test(field.name)
    ? getNextDefaultFieldName(existingNames)
    : getUniqueFieldName(field.name, existingNames);

  return {
    ...field,
    name: nextFieldName,
    ...(isOptionGroupType(field.type) && field.groupName === field.name
      ? {
        groupName: nextFieldName,
      }
      : {}),
  };
}

function getOffsetRect(rect: PdfPageRect): PdfPageRect {
  return {
    ...rect,
    x: rect.x + 12,
    y: rect.y - 12,
  };
}

export type BuilderStateAction =
  | {
    type: 'selectField';
    fieldId: string | null;
    multi?: boolean;
  }
  | {
    type: 'setZoom';
    zoom: number;
  }
  | {
    type: 'loadFields';
    fields: PdfTemplateField[];
  }
  | {
    type: 'loadDocument';
    document: PdfTemplateDocument;
    template?: Partial<PdfTemplateSummary>;
  }
  | {
    type: 'addField';
    field: PdfTemplateField;
  }
  | {
    type: 'removeField';
    fieldId: string;
  }
  | {
    type: 'removeFields';
    fieldIds: string[];
  }
  | {
    type: 'updateSelectedFieldType';
    fieldType: string;
  }
  | {
    type: 'updateSelectedFieldName';
    fieldName: string;
  }
  | {
    type: 'updateSelectedRadioGroupName';
    groupName: string;
  }
  | {
    type: 'updateSelectedCheckboxGroupName';
    groupName: string;
  }
  | {
    type: 'updateSelectedFieldAlias';
    aliasId: string | null;
  }
  | {
    type: 'updateSelectedFieldVerticalAlignment';
    alignment: string | null;
  } | {
    type: 'updateSelectedFieldHorizontalAlignment';
    alignment: string | null;
  }
  | {
    type: 'updateSelectedFieldFontSize';
    fontSize: number | null;
  }
  | {
    type: 'updateSelectedFieldAssignee';
    assignee: FormFieldAssignee;
  }
  | {
    type: 'updateSelectedFieldRequired';
    isRequired: boolean;
  }
  | {
    type: 'coerceFieldAssignees';
    assignee: FormFieldAssignee;
  }
  | {
    type: 'addRadioGroupOption';
    field: PdfTemplateField;
  }
  | {
    type: 'addCheckboxGroupOption';
    field: PdfTemplateField;
  }
  | {
    type: 'duplicateField';
    field: PdfTemplateField;
  }
  | {
    type: 'duplicateFields';
    fields: PdfTemplateField[];
  }
  | {
    type: 'replaceFields';
    fields: PdfTemplateField[];
  }
  | {
    type: 'restoreHistorySnapshot';
    fields: PdfTemplateField[];
    selectedFieldId: string | null;
    selectedFieldIds: string[];
  }
  | {
    type: 'restoreFieldHistoryScope';
    scopeId: string;
    fields: PdfTemplateField[];
  }
  | {
    type: 'updateFormTitle';
    title: string;
  }
  | {
    type: 'updateFieldRect';
    fieldId: string;
    rect: PdfTemplateField['rect'];
  }
  | {
    type: 'updateFieldPosition';
    fieldId: string;
    pageNumber: number;
    rect: PdfTemplateField['rect'];
  };

export function createInitialFormBuilderState(
  formIdString: string | undefined,
  document: PdfTemplateDocument,
  template?: Partial<PdfTemplateSummary>,
  fields?: PdfTemplateField[],
): PdfTemplateBuilderState {
  const initialFields = fields?.length ? fields : [{
    id: 'field-1',
    name: 'New 1',
    type: 'shortText',
    pageNumber: 0,
    rect: {
      x: 285,
      y: 210,
      width: 180,
      height: 34,
    },
    required: false,
  }];

  return {
    template: {
      id: formIdString ?? 'local-template',
      title: template?.title ?? document.fileName,
      status: 'draft',
      isDraft: true,
      updatedAt: 'Draft in progress',
      pageCount: 1,
      ...template,
    },
    document,
    selectedFieldId: null,
    selectedFieldIds: [],
    zoom: 1,
    fields: initialFields,
  };
}

export function builderStateReducer(
  state: PdfTemplateBuilderState,
  action: BuilderStateAction,
): PdfTemplateBuilderState {
  let nextState = state;

  switch (action.type) {
    case 'selectField':
      if (action.fieldId === null) {
        nextState = {
          ...state,
          selectedFieldId: null,
          selectedFieldIds: [],
        };
        break;
      }

      if (action.multi) {
        nextState = {
          ...state,
          selectedFieldId: action.fieldId,
          selectedFieldIds: state.selectedFieldIds.includes(action.fieldId)
            ? state.selectedFieldIds
            : [...state.selectedFieldIds, action.fieldId],
        };
        break;
      }

      nextState = {
        ...state,
        selectedFieldId: action.fieldId,
        selectedFieldIds: [action.fieldId],
      };
      break;
    case 'setZoom':
      nextState = {
        ...state,
        zoom: action.zoom,
      };
      break;
    case 'loadFields':
      nextState = {
        ...state,
        fields: action.fields,
        selectedFieldId: null,
        selectedFieldIds: [],
      };
      break;
    case 'loadDocument':
      nextState = {
        ...state,
        document: action.document,
        template: {
          ...state.template,
          title: action.template?.title ?? action.document.fileName,
          ...action.template,
        },
      };
      break;
    case 'addField': {
      const nextField = createFieldWithUniqueName(action.field, state.fields);
      nextState = {
        ...state,
        fields: [...state.fields, nextField],
        selectedFieldId: nextField.id,
        selectedFieldIds: [nextField.id],
      };
      break;
    }
    case 'removeField': {
      const fields = state.fields.filter((field) => field.id !== action.fieldId);
      const selectedFieldIds = state.selectedFieldIds.filter((fieldId) => fieldId !== action.fieldId);
      nextState = {
        ...state,
        fields,
        selectedFieldId: state.selectedFieldId === action.fieldId
          ? (selectedFieldIds.at(-1) ?? null)
          : state.selectedFieldId,
        selectedFieldIds,
      };
      break;
    }
    case 'removeFields': {
      const removedFieldIds = new Set(action.fieldIds);
      const fields = state.fields.filter((field) => !removedFieldIds.has(field.id));
      const selectedFieldIds = state.selectedFieldIds.filter((fieldId) => !removedFieldIds.has(fieldId));

      nextState = {
        ...state,
        fields,
        selectedFieldId: selectedFieldIds.at(-1) ?? null,
        selectedFieldIds,
      };
      break;
    }
    case 'updateSelectedFieldType':
      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          field.id === state.selectedFieldId
            ? {
              ...field,
              type: action.fieldType,
              groupId: getGroupIdForFieldType(field, action.fieldType),
              groupName: isOptionGroupType(action.fieldType)
                ? (field.groupName ?? field.name)
                : null,
              aliasId: canSetAutoFillAlias(action.fieldType) ? field.aliasId : null,
            }
            : field
        )),
      };
      break;
    case 'updateSelectedFieldName':
      nextState = {
        ...state,
        fields: state.fields.map((field) => {
          const selectedField = state.fields.find(
            (currentField) => currentField.id === state.selectedFieldId,
          );
          const isSelectedField = field.id === state.selectedFieldId;

          if (!isSelectedField) {
            return field;
          }

          if (!selectedField || !isOptionGroupType(selectedField.type)) {
            return {
              ...field,
              name: action.fieldName,
            };
          }

          const groupId = getOptionGroupId(selectedField);
          const siblingNames = state.fields
            .filter(
              (currentField) => isOptionGroupType(currentField.type)
                && getOptionGroupId(currentField) === groupId
                && currentField.id !== selectedField.id,
            )
            .map((currentField) => currentField.name);

          return {
            ...field,
            name: getUniqueFieldName(action.fieldName, siblingNames),
          };
        }),
      };
      break;
    case 'updateSelectedRadioGroupName':
    case 'updateSelectedCheckboxGroupName': {
      const selectedField = state.fields.find((field) => field.id === state.selectedFieldId);
      const groupType = action.type === 'updateSelectedCheckboxGroupName'
        ? 'checkboxGroup'
        : 'radioGroup';
      const groupId = selectedField?.groupId ?? selectedField?.id ?? null;

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          field.type === groupType && (field.groupId ?? field.id) === groupId
            ? {
              ...field,
              groupId,
              groupName: action.groupName,
            }
            : field
        )),
      };
      break;
    }
    case 'updateSelectedFieldAlias':
      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          field.id === state.selectedFieldId
            ? {
              ...field,
              aliasId: action.aliasId,
            }
            : field
        )),
      };
      break;
    case 'updateSelectedFieldVerticalAlignment': {
      const targetFieldIds = getTargetFieldIds(state);

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          targetFieldIds.has(field.id) && fieldSupportsAlignment(field.type)
            ? { ...field, verticalAlignment: action.alignment }
            : field
        )),
      };
      break;
    }
    case 'updateSelectedFieldHorizontalAlignment': {
      const targetFieldIds = getTargetFieldIds(state);

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          targetFieldIds.has(field.id) && fieldSupportsAlignment(field.type)
            ? { ...field, horizontalAlignment: action.alignment }
            : field
        )),
      };
      break;
    }
    case 'updateSelectedFieldFontSize': {
      const targetFieldIds = getTargetFieldIds(state);

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          targetFieldIds.has(field.id)
            ? { ...field, fontSize: action.fontSize }
            : field
        )),
      };
      break;
    }
    case 'updateSelectedFieldAssignee': {
      const targetFieldIds = getTargetFieldIds(state);
      const fieldsToUpdate = new Set<string>(targetFieldIds);

      targetFieldIds.forEach((fieldId) => {
        const field = state.fields.find((item) => item.id === fieldId);

        if (field && isOptionGroupType(field.type)) {
          getOptionGroupFields(state.fields, field)
            .forEach((groupField) => fieldsToUpdate.add(groupField.id));
        }
      });

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          fieldsToUpdate.has(field.id)
            ? { ...field, assignee: action.assignee }
            : field
        )),
      };
      break;
    }
    case 'updateSelectedFieldRequired': {
      const selectedField = state.fields.find((field) => field.id === state.selectedFieldId);
      if (!selectedField || !canToggleFieldOptional(selectedField.type)) {
        break;
      }

      if (isOptionGroupType(selectedField.type)) {
        const groupFields = getOptionGroupFields(state.fields, selectedField);
        const updatedGroupFields = applyOptionGroupIsRequired(
          groupFields,
          selectedField.type,
          action.isRequired,
        );
        const updatedGroupFieldIds = new Set(updatedGroupFields.map((field) => field.id));

        nextState = {
          ...state,
          fields: state.fields.map((field) => {
            if (!updatedGroupFieldIds.has(field.id)) {
              return field;
            }

            return updatedGroupFields.find((groupField) => groupField.id === field.id) ?? field;
          }),
        };
        break;
      }

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          field.id === state.selectedFieldId
            ? {
              ...field,
              isRequired: action.isRequired,
              required: action.isRequired,
            }
            : field
        )),
      };
      break;
    }
    case 'coerceFieldAssignees':
      nextState = {
        ...state,
        fields: state.fields.map((field) => ({
          ...field,
          assignee: action.assignee,
        })),
      };
      break;
    case 'addRadioGroupOption':
    case 'addCheckboxGroupOption':
      nextState = {
        ...state,
        fields: [
          ...state.fields,
          createFieldWithUniqueName(action.field, state.fields),
        ],
        selectedFieldId: action.field.id,
        selectedFieldIds: [action.field.id],
      };
      break;
    case 'duplicateField':
      nextState = {
        ...state,
        fields: [...state.fields, action.field],
        selectedFieldId: action.field.id,
        selectedFieldIds: [action.field.id],
      };
      break;
    case 'duplicateFields':
      nextState = {
        ...state,
        fields: [...state.fields, ...action.fields],
        selectedFieldId: action.fields.at(-1)?.id ?? null,
        selectedFieldIds: action.fields.map((field) => field.id),
      };
      break;
    case 'replaceFields': {
      const replacementFields = new Map(
        action.fields.map((field) => [field.id, field]),
      );

      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          replacementFields.get(field.id) ?? field
        )),
      };
      break;
    }
    case 'restoreHistorySnapshot':
      nextState = {
        ...state,
        fields: action.fields,
        selectedFieldId: action.selectedFieldId,
        selectedFieldIds: action.selectedFieldIds,
      };
      break;
    case 'restoreFieldHistoryScope': {
      const scopedFields = getFieldHistoryScopeByScopeId(action.scopeId, state.fields);
      const scopedFieldIds = new Set(scopedFields.map((field) => field.id));
      const firstScopedFieldIndex = state.fields.findIndex(
        (field) => scopedFieldIds.has(field.id),
      );
      const remainingFields = state.fields.filter(
        (field) => !scopedFieldIds.has(field.id),
      );

      if (firstScopedFieldIndex < 0) {
        nextState = {
          ...state,
          fields: [...remainingFields, ...action.fields],
        };
        break;
      }

      nextState = {
        ...state,
        fields: [
          ...remainingFields.slice(0, firstScopedFieldIndex),
          ...action.fields,
          ...remainingFields.slice(firstScopedFieldIndex),
        ],
      };
      break;
    }
    case 'updateFormTitle':
      nextState = {
        ...state,
        template: {
          ...state.template,
          title: action.title,
        },
      };
      break;
    case 'updateFieldRect':
      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          field.id === action.fieldId
            ? {
              ...field,
              rect: action.rect,
            }
            : field
        )),
      };
      break;
    case 'updateFieldPosition':
      nextState = {
        ...state,
        fields: state.fields.map((field) => (
          field.id === action.fieldId
            ? {
              ...field,
              pageNumber: action.pageNumber,
              rect: action.rect,
            }
            : field
        )),
      };
      break;
    default:
      nextState = state;
      break;
  }

  return nextState;
}

export function createDuplicatedField(
  field: PdfTemplateField,
  existingFields: PdfTemplateField[],
  createId: () => string,
): PdfTemplateField {
  if (isOptionGroupType(field.type)) {
    const groupId = field.groupId ?? field.id;
    const siblingNames = existingFields
      .filter(
        (existingField) => isOptionGroupType(existingField.type)
          && (existingField.groupId ?? existingField.id) === groupId,
      )
      .map((existingField) => existingField.name);

    return {
      ...field,
      id: createId(),
      name: getUniqueFieldName(field.name, siblingNames),
      rect: getOffsetRect(field.rect),
      required: false,
      isRequired: false,
    };
  }

  return {
    ...field,
    id: createId(),
    rect: getOffsetRect(field.rect),
  };
}
